import {
  ArrowDownloadRegular,
  ArrowUploadRegular,
  MoreVerticalRegular,
} from '@fluentui/react-icons';
import {
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components';
import React, { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePapaParse } from 'react-papaparse';

import { Button } from '../../Button';
import ConfirmationDialog from '../../ConfirmationDialog/ConfirmationDialog';
import { CSVConverterProvider } from '../CSVConverter/CSVConverterProvider';
import { CourseExport } from '../CourseMapping.enums';
import {
  CourseTimetableMappingTransferComponentProps,
  dialogTextData,
} from '../CourseMapping.interfaces';
import { buildGroupsExport, downloadCSV } from '../CourseMapping.utils';
import { UNPARSE_CONFIG } from '../csv.config';

export const CourseMappingTransferComponent: FC<CourseTimetableMappingTransferComponentProps> = (
  props,
) => {
  const {
    timetableCourseSelection,
    cloudCourseSelection,
    csvAssignmentsExportData,
    dataImported,
    errorOccured,
  } = props;
  const { jsonToCSV } = usePapaParse();
  const { t } = useTranslation();
  const [dialogProps, setDialogProps] = useState<dialogTextData>({
    headerText: '',
    content: '',
  });
  const [downloadType, setDownloadType] = useState<CourseExport | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  const openExportDialog = (dialog: dialogTextData, type: CourseExport) => {
    setDownloadType(type);
    setDialogProps(dialog);
    setDialogOpen(true);
  };

  const closeExportDialog = () => {
    setDownloadType(null);
    setDialogProps({ headerText: '', content: '' });
    setDialogOpen(false);
  };

  const filesToDownload = (): { fileName: string; data: string }[] => {
    const assignments = {
      fileName: t('settings:courseimport:courseMappingTransferComponent:course_mappings', {
        defaultValue: 'Kurszuweisungen',
      }),
      data: JSON.stringify(csvAssignmentsExportData),
    };
    const groups = {
      fileName: t('settings:courseimport:courseMappingTransferComponent:mnspro_courses', {
        defaultValue: 'MNS Pro Kurse',
      }),
      data: JSON.stringify(buildGroupsExport(cloudCourseSelection)),
    };
    const timetable = {
      fileName: t('settings:courseimport:courseMappingTransferComponent:timetable_courses', {
        defaultValue: 'Stundenplankurse',
      }),
      data: JSON.stringify(timetableCourseSelection),
    };
    switch (downloadType) {
      case CourseExport.all:
        return [assignments, groups, timetable];
      case CourseExport.assignment:
        return [assignments];
      case CourseExport.mnsProCourse:
        return [groups];
      case CourseExport.timetableCourses:
        return [timetable];
      default:
        return [];
    }
  };

  const handleConfirmDialog = () => {
    filesToDownload().forEach((file) => {
      downloadCSV(
        file.fileName,
        file.data,
        UNPARSE_CONFIG,
        jsonToCSV as (json: unknown, options?: unknown) => string,
      );
    });
    closeExportDialog();
  };

  return (
    <>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button appearance="transparent" icon={<MoreVerticalRegular />} />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              icon={<ArrowUploadRegular />}
              onClick={() => hiddenFileInput.current?.click()}
            >
              {t('import', { defaultValue: 'Import' })}
            </MenuItem>
            <MenuDivider />
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <MenuItem icon={<ArrowDownloadRegular />}>
                  {t('export', { defaultValue: 'Export' })}
                </MenuItem>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem
                    icon={<ArrowDownloadRegular />}
                    onClick={() =>
                      openExportDialog(
                        {
                          content: t(
                            'settings:courseimport:courseMappingTransferComponent:download_timetable_courses_question',
                            {
                              defaultValue: 'Möchten Sie die Stundenplankurse herunterladen?',
                            },
                          ),
                          headerText: t(
                            'settings:courseimport:courseMappingTransferComponent:export_timetable_courses',
                            { defaultValue: 'Stundenplankurse exportieren' },
                          ),
                        },
                        CourseExport.timetableCourses,
                      )
                    }
                  >
                    {t('settings:courseimport:courseMappingTransferComponent:timetable_courses', {
                      defaultValue: 'Stundenplankurse',
                    })}
                  </MenuItem>
                  <MenuItem
                    icon={<ArrowDownloadRegular />}
                    onClick={() =>
                      openExportDialog(
                        {
                          content: t(
                            'settings:courseimport:courseMappingTransferComponent:download_school_courses_question',
                            {
                              defaultValue: 'Möchten Sie die Kurse Ihrer Schule herunterladen?',
                            },
                          ),
                          headerText: t(
                            'settings:courseimport:courseMappingTransferComponent:export_school_courses',
                            { defaultValue: 'Schulkurse exportieren' },
                          ),
                        },
                        CourseExport.mnsProCourse,
                      )
                    }
                  >
                    {t('settings:courseimport:courseMappingTransferComponent:mnspro_courses', {
                      defaultValue: 'MNS Pro Kurse',
                    })}
                  </MenuItem>
                  <MenuItem
                    icon={<ArrowDownloadRegular />}
                    onClick={() =>
                      openExportDialog(
                        {
                          content: t(
                            'settings:courseimport:courseMappingTransferComponent:download_course_mappings_question',
                            {
                              defaultValue: 'Möchten Sie die Kurszuweisungen herunterladen?',
                            },
                          ),
                          headerText: t(
                            'settings:courseimport:courseMappingTransferComponent:export_course_mappings',
                            { defaultValue: 'Kurszuweisungen exportieren' },
                          ),
                        },
                        CourseExport.assignment,
                      )
                    }
                  >
                    {t('settings:courseimport:courseMappingTransferComponent:course_mappings', {
                      defaultValue: 'Kurszuweisungen',
                    })}
                  </MenuItem>
                  <MenuItem
                    icon={<ArrowDownloadRegular />}
                    onClick={() =>
                      openExportDialog(
                        {
                          content: t(
                            'settings:courseimport:courseMappingTransferComponent:download_all_files_question',
                            {
                              defaultValue: 'Möchten Sie alle Dateien herunterladen?',
                            },
                          ),
                          headerText: t(
                            'settings:courseimport:courseMappingTransferComponent:export_all',
                            { defaultValue: 'Alles exportieren' },
                          ),
                        },
                        CourseExport.all,
                      )
                    }
                  >
                    {t('settings:courseimport:courseMappingTransferComponent:all_lists', {
                      defaultValue: 'Alle Listen',
                    })}
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </MenuList>
        </MenuPopover>
      </Menu>
      <ConfirmationDialog
        content={dialogProps.content}
        headerText={dialogProps.headerText}
        cancelButtonText={t('cancel', { defaultValue: 'Abbrechen' })}
        confirmButtonText={t('approve', { defaultValue: 'Bestätigen' })}
        open={dialogOpen}
        showCloseButton={false}
        onConfirm={handleConfirmDialog}
        onCancel={closeExportDialog}
      />
      <CSVConverterProvider
        complete={(rows) => dataImported?.(rows)}
        error={errorOccured}
        inputRef={hiddenFileInput}
      />
    </>
  );
};
